// Closing-gate Bypass (Escrow) query + mutation hooks.
//
// ADMIN-only break-glass to force-complete a closing gate for a TEST deal so
// QA can reach loan boarding without a real, funded escrow close. Only the
// Escrow Setup gate can be bypassed today.
//
// The bypass writes a real EscrowSetup row (issuance + funding-verified stamped
// with a bypassedAt marker), so on success we invalidate the escrow query key
// the Underwriting Section 2 + Closing Step 3 surfaces read — they re-render to
// the "bypassed" state — plus the gate-bypass log reads (per-deal + recent).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dealGateBypassApi } from "@/api/dealFlow/client";
import type {
  DealGateBypassLogDto,
  DealGateBypassResultDto,
} from "@/api/dealFlow/types";
import { dealFlowKeys } from "./useDealFlow";

/** ADMIN-only — force-complete the Escrow Setup gate for a TEST deal. */
export function useBypassEscrowGate(dealId: string) {
  const qc = useQueryClient();
  return useMutation<DealGateBypassResultDto, Error, string>({
    mutationFn: (reason: string) => dealGateBypassApi.bypassEscrow(dealId, reason),
    onSuccess: () => {
      // The Underwriting Section 2 + Closing Step 3 surfaces read the escrow
      // row; a bypassed row now exists → flip them to the legacy + banner state
      // without a manual refresh.
      qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) });
      // Both the per-deal and cross-deal bypass logs gain a row.
      qc.invalidateQueries({ queryKey: dealFlowKeys.gateBypass });
    },
  });
}

/** Console variant — the deal id is a mutation variable (typed in by the
 *  admin) rather than fixed at hook construction. */
export function useBypassEscrowGateForInput() {
  const qc = useQueryClient();
  return useMutation<
    DealGateBypassResultDto,
    Error,
    { dealId: string; reason: string }
  >({
    mutationFn: ({ dealId, reason }) =>
      dealGateBypassApi.bypassEscrow(dealId, reason),
    onSuccess: (_result, { dealId }) => {
      qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) });
      qc.invalidateQueries({ queryKey: dealFlowKeys.gateBypass });
    },
  });
}

/** Per-deal bypass history, most-recent first. */
export function useDealGateBypassLog(dealId: string | undefined) {
  return useQuery<DealGateBypassLogDto[]>({
    queryKey: dealFlowKeys.gateBypassForDeal(dealId ?? ""),
    queryFn: () => dealGateBypassApi.listForDeal(dealId!),
    enabled: !!dealId,
  });
}

/** Cross-deal recent bypasses, most-recent first. */
export function useRecentGateBypasses(take = 100) {
  return useQuery<DealGateBypassLogDto[]>({
    queryKey: dealFlowKeys.gateBypassRecent(take),
    queryFn: () => dealGateBypassApi.listRecent(take),
  });
}
